import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Save, Plus, X } from "lucide-react";
import { enabledDepartments } from "./useOrg";
import { PermissionMatrix } from "./PermissionMatrix";

const pwValid = (p) => p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p);
const NONE = "none";
const blank = { full_name: "", mobile: "", email: "", assignments: [{ department: "", role: "" }], permissions: [], reporting_manager_id: NONE, branch_id: NONE };

function Pick({ label, value, onChange, items, testid, disabled }) {
  return (
    <div>{label && <Label>{label}</Label>}
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={testid} className={label ? "mt-1" : ""}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Assignments({ value, onChange, depts }) {
  const upd = (i, patch) => onChange(value.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  return (
    <div className="space-y-2" data-testid="employee-assignments">
      <Label>Department & Designation / Role *</Label>
      {value.map((a, i) => {
        const d = depts.find((x) => x.key === a.department);
        return (
          <div key={a.key ?? `${a.department}|${a.role}|${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
            <Pick value={a.department} testid={`employee-dept-select-${i}`} onChange={(v) => upd(i, { department: v, role: "" })} items={depts.map((x) => ({ value: x.key, label: x.name }))} />
            <Pick value={a.role} testid={`employee-role-select-${i}`} onChange={(v) => upd(i, { role: v })} items={(d?.roles || []).map((r) => ({ value: r.name, label: r.name }))} />
            <Button type="button" variant="ghost" size="sm" disabled={value.length === 1} onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`employee-assignment-remove-${i}`}><X className="h-4 w-4" /></Button>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { key: Date.now(), department: "", role: "" }])} data-testid="employee-add-role-btn"><Plus className="mr-1 h-4 w-4" /> Add another role</Button>
    </div>
  );
}

export function EmployeeDialog({ open, onOpenChange, data, employee, onSaved, onInvite }) {
  const { user } = useAuth();
  const owner = user?.role === "owner";
  const scope = owner ? null : (user?.assignments || []).map((a) => a.department);
  const [f, setF] = useState(blank);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setPw(""); setPw2("");
    setF(employee ? {
      full_name: employee.name, mobile: employee.mobile, email: employee.email || "",
      assignments: employee.assignments?.length ? employee.assignments : [{ department: "", role: "" }],
      permissions: employee.permissions || [],
      reporting_manager_id: employee.reporting_manager_id || NONE, branch_id: employee.branch_id || NONE,
    } : { ...blank, assignments: [{ department: scope?.length === 1 ? scope[0] : "", role: "" }] });
  }, [open, employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const depts = enabledDepartments(data).filter((d) => !scope || scope.includes(d.key));
  const allowed = owner ? null : (user?.permissions || []).filter((p) => !p.startsWith("org_settings."));
  const managers = [
    ...(data.owner ? [{ value: data.owner.id, label: `${data.owner.name} (Super Admin)` }] : []),
    ...data.employees.filter((e) => e.id !== employee?.id).map((e) => ({ value: e.id, label: `${e.name} — ${e.job_role || "Employee"}` })),
  ];

  const setAssignments = (list) => {
    const defaults = new Set(f.permissions);
    list.forEach((a) => {
      const r = depts.find((d) => d.key === a.department)?.roles.find((x) => x.name === a.role);
      const was = f.assignments.some((o) => o.department === a.department && o.role === a.role);
      if (r && !was) (r.default_permissions || []).forEach((p) => (!allowed || allowed.includes(p)) && defaults.add(p));
    });
    setF((p) => ({ ...p, assignments: list, permissions: [...defaults] }));
  };

  const save = async () => {
    if (!f.full_name.trim()) { toast.error("Enter the employee's name"); return; }
    if (!f.mobile.startsWith("+")) { toast.error("Use international format e.g. +9198..."); return; }
    const assigns = f.assignments.filter((a) => a.department && a.role).map(({ department, role }) => ({ department, role }));
    if (!assigns.length) { toast.error("Choose a department and role"); return; }
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) { toast.error("Enter a valid email (e.g. name@company.com) or leave it blank"); return; }
    if ((!employee || pw) && !pwValid(pw)) { toast.error("Password must be 8+ characters with uppercase, lowercase and a number"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    const body = {
      full_name: f.full_name, mobile: f.mobile, email: f.email.trim() || null, password: pw || null, assignments: assigns, permissions: f.permissions,
      reporting_manager_id: f.reporting_manager_id === NONE ? null : f.reporting_manager_id,
      branch_id: f.branch_id === NONE ? null : f.branch_id,
    };
    setBusy(true);
    try {
      if (employee) await api.put(`/org/employees/${employee.id}`, body);
      else {
        const { data: r } = await api.post("/org/employees", body);
        onInvite?.({ name: body.full_name, mobile: body.mobile, email: body.email, password: pw, emailSent: r.invite_email_sent });
      }
      toast.success(employee ? "Employee updated" : "Employee added");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader><DialogTitle>{employee ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Employee Name *</Label><Input data-testid="employee-name-input" className="mt-1" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
          <div><Label>Mobile Number * (sign-in ID)</Label><Input data-testid="employee-mobile-input" className="mt-1" placeholder="+919812345678" value={f.mobile} disabled={!!employee} onChange={(e) => set("mobile", e.target.value)} /></div>
          <div><Label>Email Address</Label><Input data-testid="employee-email-input" type="email" className="mt-1" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
          <Pick label="Reporting Manager" value={f.reporting_manager_id} testid="employee-manager-select" onChange={(v) => set("reporting_manager_id", v)} items={[{ value: NONE, label: "None" }, ...managers]} />
          <Pick label="Branch / Location" value={f.branch_id} testid="employee-branch-select" onChange={(v) => set("branch_id", v)} items={[{ value: NONE, label: "Head office" }, ...data.branches.map((b) => ({ value: b.id, label: b.name }))]} />
        </div>
        <Assignments value={f.assignments} onChange={setAssignments} depts={depts} />
        <div>
          <p className="mb-2 text-sm font-semibold text-stone-800">Permissions <span className="font-normal text-stone-400">— suggested from the role; access is set here, not by the job title</span></p>
          <PermissionMatrix modules={data.permissions} value={f.permissions} onChange={(v) => set("permissions", v)} allowed={allowed} testid="employee-permissions" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>{employee ? "New Password (optional)" : "Password *"}</Label><Input data-testid="employee-password-input" type="password" className="mt-1" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
          <div><Label>Re-enter Password</Label><Input data-testid="employee-password2-input" type="password" className="mt-1" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
        </div>
        <p className="text-xs text-stone-500">The employee signs in with this mobile number and password. Changing the password signs them out everywhere.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="employee-cancel-btn">Cancel</Button>
          <Button data-testid="employee-save-btn" disabled={busy} onClick={save} className="bg-[#0D5C3A] hover:bg-[#084229]">
            {employee ? <><Save className="mr-1 h-4 w-4" /> Save</> : <><UserPlus className="mr-1 h-4 w-4" /> Add employee</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
